import { apiClient } from './apiClient';
import {
    Contract,
    CreateContractInput,
    ContractWithUsers,
    EditContractInput,
    Milestone,
    CreateMilestoneInput
} from '../types/contract';


// Helper to map backend CamelCase to frontend snake_case
const mapContractResponse = (data: any): Contract => {
    return {
        contract_id: data.contractId,
        brief_id: data.briefId,
        client_user_id: data.clientUserId,
        creator_user_id: data.creatorUserId,
        title: data.title,
        description: data.description,
        requirements: data.requirements,
        terms: data.terms,
        total_amount: data.totalAmount,
        currency: data.currency,
        estimated_delivery_date: data.estimatedDeliveryDate,
        status: data.status,
        client_signed_at: data.clientSignedAt,
        creator_signed_at: data.creatorSignedAt,
        completed_at: data.completedAt,
        cancelled_at: data.cancelledAt,
        cancelled_by: data.cancelledBy,
        cancellation_reason: data.cancellationReason,
        esign_flow_id: data.esignFlowId,
        esign_doc_id: data.esignDocId,
        creator_sign_status: data.creatorSignStatus,
        client_sign_status: data.clientSignStatus,
        custom_pdf_oss_key: data.customPdfOssKey,
        invoice_oss_key: data.invoiceOssKey,
        invoice_file_name: data.invoiceFileName,
        invoice_uploaded_at: data.invoiceUploadedAt,
        payment_status: data.paymentStatus,
        alipay_trade_no: data.alipayTradeNo,
        payment_deadline_at: data.paymentDeadlineAt,
        paid_at: data.paidAt,
        platform_fee_rate: data.platformFeeRate,
        created_at: data.createdAt,
        updated_at: data.updatedAt
    };
};

const mapMilestoneResponse = (data: any): Milestone => {
    return {
        milestone_id: data.milestoneId,
        contract_id: data.contractId,
        title: data.title,
        description: data.description,
        order_index: data.orderIndex,
        payment_amount: data.paymentAmount,
        requires_deliverable: data.requiresDeliverable,
        deliverable_urls: data.deliverableUrls || [],
        estimated_completion_date: data.estimatedCompletionDate,
        status: data.status,
        submitted_at: data.submittedAt,
        approved_at: data.approvedAt,
        revision_feedback: data.revisionFeedback,
        submission_count: data.submissionCount,
        completed_at: data.completedAt,
        cancelled_at: data.cancelledAt,
        payment_status: data.paymentStatus,
        released_amount: data.releasedAmount,
        platform_fee_amount: data.platformFeeAmount,
        alipay_split_no: data.alipaySplitNo,
        released_at: data.releasedAt,
        refunded_at: data.refundedAt,
        created_at: data.createdAt,
        updated_at: data.updatedAt,
        has_penalty: data.hasPenalty,
        penalty_type: data.penaltyType,
        penalty_value: data.penaltyValue,
        penalty_description: data.penaltyDescription
    };
};

export const contractService = {
    // Create Contract
    createContract: async (data: CreateContractInput) => {
        // Map frontend snake_case to backend CamelCase
        const payload = {
            briefId: data.brief_id,
            clientUserId: data.client_user_id,
            title: data.title,
            description: data.description,
            requirements: data.requirements,
            terms: data.terms,
            totalAmount: data.total_amount,
            currency: data.currency,
            estimatedDeliveryDate: data.estimated_delivery_date,
            status: data.status,
            customPdfOssKey: data.custom_pdf_oss_key,
            reviewSlaHours: data.review_sla_hours,
            milestones: data.milestones.map(m => ({
                title: m.title,
                description: m.description,
                orderIndex: m.order_index,
                paymentAmount: m.payment_amount,
                requiresDeliverable: m.requires_deliverable,
                hasPenalty: m.has_penalty,
                penaltyType: m.penalty_type,
                penaltyValue: m.penalty_value,
                penaltyDescription: m.penalty_description,
                estimatedCompletionDate: m.estimated_completion_date
            }))
        };
        const endpoint = data.status === 'DRAFT' ? '/api/v1/contracts/drafts' : '/api/v1/contracts';
        return apiClient.post<Contract>(endpoint, payload);
    },

    // Get Contract Details
    getContract: async (contractId: string) => {
        const response = await apiClient.get<{
            contract: any;
            milestones: any[];
            progress: any;
        }>(`/api/v1/contracts/${contractId}`);

        if (response.success && response.data) {
            return {
                ...response,
                data: {
                    ...response.data,
                    contract: mapContractResponse(response.data.contract),
                    milestones: response.data.milestones.map(mapMilestoneResponse)
                }
            };
        }
        return response;
    },

    // Update Contract
    updateContract: async (contractId: string, data: EditContractInput) => {
        const payload: any = {};
        if (data.title !== undefined) payload.title = data.title;
        if (data.description !== undefined) payload.description = data.description;
        if (data.requirements !== undefined) payload.requirements = data.requirements;
        if (data.terms !== undefined) payload.terms = data.terms;
        if (data.total_amount !== undefined) payload.totalAmount = data.total_amount;
        if (data.estimated_delivery_date !== undefined) payload.estimatedDeliveryDate = data.estimated_delivery_date;
        if (data.status !== undefined) payload.status = data.status;

        if (data.milestones) {
            payload.milestones = data.milestones.map(m => ({
                title: m.title,
                description: m.description,
                orderIndex: m.order_index,
                paymentAmount: m.payment_amount,
                requiresDeliverable: m.requires_deliverable,
                hasPenalty: m.has_penalty,
                penaltyType: m.penalty_type,
                penaltyValue: m.penalty_value,
                penaltyDescription: m.penalty_description,
                estimatedCompletionDate: m.estimated_completion_date
            }));
        }
        return apiClient.put<Contract>(`/api/v1/contracts/${contractId}`, payload);
    },

    // Publish Contract
    publishContract: async (contractId: string) => {
        return apiClient.post<{ success: boolean; contract: Contract }>(`/api/v1/contracts/${contractId}/publish`);
    },

    // Accept Contract
    acceptContract: async (contractId: string) => {
        return apiClient.post<{ success: boolean; contract: Contract }>(`/api/v1/contracts/${contractId}/accept`);
    },

    // Create e签宝 signing flow
    createSigningFlow: async (contractId: string) => {
        return apiClient.post<{ flowId: string }>(`/api/v1/contracts/${contractId}/esign`);
    },

    // Get signing URL for current user
    getSignUrl: async (contractId: string, redirectUrl?: string) => {
        const params = redirectUrl ? `?redirectUrl=${encodeURIComponent(redirectUrl)}` : '';
        return apiClient.get<{ signUrl: string }>(`/api/v1/contracts/${contractId}/sign-url${params}`);
    },

    // Get signed PDF download URL
    getSignedPdfUrl: async (contractId: string) => {
        return apiClient.get<{ downloadUrl: string }>(`/api/v1/contracts/${contractId}/signed-pdf`);
    },

    // Refresh signing status by polling e签宝
    refreshSignStatus: async (contractId: string) => {
        return apiClient.post<{ clientSignStatus: string; creatorSignStatus: string; contract: any }>(
            `/api/v1/contracts/${contractId}/refresh-sign-status`
        );
    },

    // Upload custom contract PDF. Returns OSS key to include in create contract request.
    uploadCustomPdf: async (file: File) => {
        const fileBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
        return apiClient.post<{ ossKey: string; fileName: string; size: number }>(
            '/api/v1/contracts/upload-pdf',
            { fileName: file.name, fileBase64 }
        );
    },

    // Stream uploaded custom contract PDF as a blob and return an
    // in-memory object URL the iframe can render. The backend pipes the
    // OSS object back with Content-Disposition: inline so this never
    // triggers a download.
    getCustomPdfPreviewUrl: async (ossKey: string, fileName?: string) => {
        const res = await apiClient.postBlob(
            '/api/v1/contracts/upload-pdf-preview',
            { ossKey, fileName }
        );
        if (!res.success || !res.data) {
            return { success: false, error: (res as any).error || 'Failed to load preview' };
        }
        const blob = res.data instanceof Blob
            ? res.data
            : new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        return {
            success: true as const,
            data: { previewUrl: url, isObjectUrl: true },
        };
    },

    // Upload invoice for a contract (creator only). Accepts PDF/JPG/PNG, max 20MB.
    uploadInvoice: async (contractId: string, file: File) => {
        const fileBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
        return apiClient.post<{ ossKey: string; fileName: string; size: number }>(
            `/api/v1/contracts/${contractId}/invoice`,
            { fileName: file.name, fileBase64 }
        );
    },

    // Get signed download URL for an uploaded invoice.
    getInvoiceUrl: async (contractId: string) => {
        return apiClient.get<{ downloadUrl: string; fileName: string; uploadedAt: number }>(
            `/api/v1/contracts/${contractId}/invoice`
        );
    },

    // Reject Contract
    rejectContract: async (contractId: string) => {
        return apiClient.post<{ success: boolean; message: string }>(`/api/v1/contracts/${contractId}/reject`);
    },

    // Request a milestone delivery date extension. Creates a PENDING amendment
    // that the OTHER party must approve.
    requestMilestoneExtension: async (
        contractId: string,
        milestoneId: string,
        newCompletionDate: number,
        reason: string,
    ) => {
        return apiClient.post<{ success: boolean; amendment: any }>(
            `/api/v1/contracts/${contractId}/milestones/${milestoneId}/extend`,
            { newCompletionDate, reason },
        );
    },

    // List all amendments (pending + decided) on a contract.
    getAmendments: async (contractId: string) => {
        return apiClient.get<{ amendments: any[]; totalCount: number }>(
            `/api/v1/contracts/${contractId}/amendments`,
        );
    },

    // Approve or reject a pending amendment (caller must be the non-proposer).
    reviewAmendment: async (amendmentId: string, approved: boolean, reviewNotes?: string) => {
        return apiClient.post<{ success: boolean; amendment: any }>(
            `/api/v1/contracts/amendments/${amendmentId}/review`,
            { approved, reviewNotes },
        );
    },

    // Cancel Contract
    cancelContract: async (contractId: string, reason: string) => {
        return apiClient.post<{ success: boolean; message: string }>(`/api/v1/contracts/${contractId}/cancel`, {
            cancellationReason: reason
        });
    },

    // Delete Contract (Drafts only)
    deleteContract: async (contractId: string) => {
        return apiClient.delete<{ success: boolean; message: string }>(`/api/v1/contracts/${contractId}`);
    },

    // Add Milestone
    addMilestone: async (contractId: string, data: CreateMilestoneInput) => {
        return apiClient.post<Milestone>(`/api/v1/contracts/${contractId}/milestones`, data);
    },

    // Start Milestone
    startMilestone: async (contractId: string, milestoneId: string) => {
        return apiClient.post<Milestone>(`/api/v1/contracts/${contractId}/milestones/${milestoneId}/start`);
    },

    // Submit Milestone
    submitMilestone: async (contractId: string, milestoneId: string, deliverableUrls: string[]) => {
        return apiClient.post<Milestone>(`/api/v1/contracts/${contractId}/milestones/${milestoneId}/submit`, { deliverableUrls });
    },

    // Approve Milestone
    approveMilestone: async (contractId: string, milestoneId: string) => {
        return apiClient.post<Milestone>(`/api/v1/contracts/${contractId}/milestones/${milestoneId}/approve`);
    },

    // Reject Milestone
    rejectMilestone: async (contractId: string, milestoneId: string, feedback: string) => {
        return apiClient.post<Milestone>(`/api/v1/contracts/${contractId}/milestones/${milestoneId}/reject`, { feedback });
    },

    // Cancel Milestone
    cancelMilestone: async (contractId: string, milestoneId: string) => {
        return apiClient.post<Milestone>(`/api/v1/contracts/${contractId}/milestones/${milestoneId}/cancel`);
    },

    // List Contracts (with filters)
    listContracts: async (filters?: {
        clientUserId?: string;
        creatorUserId?: string;
        status?: string;
        page?: number;
        limit?: number;
    }) => {
        // Build query string
        const params = new URLSearchParams();
        if (filters?.clientUserId) params.append('clientUserId', filters.clientUserId);
        if (filters?.creatorUserId) params.append('creatorUserId', filters.creatorUserId);
        if (filters?.status) params.append('status', filters.status);
        if (filters?.limit) params.append('limit', filters.limit.toString());

        const response = await apiClient.get<{ contracts: any[], totalCount: number, hasMore: boolean }>(`/api/v1/contracts?${params.toString()}`);

        if (response.success && response.data) {
            return {
                ...response,
                data: {
                    ...response.data,
                    contracts: response.data.contracts.map(mapContractResponse)
                }
            };
        }
        return response;
    }
};
